import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, X, Phone, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import WhatsAppShortcuts from "@/components/WhatsAppShortcuts";
import DetailPopup from "@/components/DetailPopup";
import AdminDeleteButton from "@/components/AdminDeleteButton";

const STATUSES = ["new","contacted","quoted","won","lost"];
const STATUS_STYLES = {
  new:       { header: "bg-[#E5ECF5] text-[#2C4763] border-l-4 border-[#3D5A80]", label: "NEW" },
  contacted: { header: "bg-[#FBEDD6] text-[#7A5117] border-l-4 border-[#D99036]", label: "CONTACTED" },
  quoted:    { header: "bg-[#EFE1F5] text-[#5A2E7A] border-l-4 border-[#8A4FBF]", label: "QUOTED" },
  won:       { header: "bg-[#DDEADE] text-[#2E4A38] border-l-4 border-[#4A7258]", label: "WON" },
  lost:      { header: "bg-[#F0EBE3] text-[#75706A] border-l-4 border-[#B3AC9E]", label: "LOST" },
};
const SOURCES = ["instagram","walk-in","referral","cold-call","website","google-ads","other"];
const CATEGORIES = ["T-Shirts","Polo Tees","Hoodies","Caps","Bags","Diaries","Corporate Gifts","Jerseys","Uniforms","Other"];

export default function Leads() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [popup, setPopup] = useState(null);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", source: "instagram", product_category: "T-Shirts", interest: "", status: "new", next_follow_up: "" });

  const load = async () => setItems((await api.get("/leads")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      toast.success("Lead added");
      setForm({ customer_name: "", phone: "", email: "", source: "instagram", product_category: "T-Shirts", interest: "", status: "new", next_follow_up: "" });
      setShowAdd(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/leads/${id}`, { status });
    load();
  };

  const mailto = (l) => {
    const subject = encodeURIComponent(`From Marsh Clothing — quick note`);
    const body = encodeURIComponent(
`Hi ${l.customer_name || "there"},

Great connecting with you regarding ${l.product_category || "your requirement"}. Please find our top catalog links below:

• Premium Custom Apparel & Corporate Uniforms — https://marshclothing.com/pages/premium-custom-apparel-corporate-uniforms
• Round-neck T-shirts — https://marshclothing.com/collections/round-neck-tshirts-custom-printed
• Polo T-shirts — https://marshclothing.com/collections/polo-tshirts

Happy to help you customise anything — colors, sizes, prints, embroidery — from 25 pieces upwards.

Warm regards,
Marsh Clothing`);
    window.open(`mailto:${l.email || ""}?subject=${subject}&body=${body}`, "_self");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Sales pipeline</div>
          <h1 className="font-display text-3xl tracking-tight">Leads</h1>
          <p className="text-sm text-[#757169] mt-1">Send WhatsApp templates or emails right from a card. Click a card for full detail.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={()=>setShowAdd(v=>!v)} data-testid="leads-add-btn">
          {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New lead</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-2 gap-4 fade-up" data-testid="leads-form">
          <div><div className="label-tiny mb-1.5">Name *</div>
            <input required className="input-field" value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} data-testid="leads-field-name" /></div>
          <div><div className="label-tiny mb-1.5">Phone</div>
            <input className="input-field" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="leads-field-phone"/></div>
          <div><div className="label-tiny mb-1.5">Email</div>
            <input type="email" className="input-field" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="leads-field-email"/></div>
          <div><div className="label-tiny mb-1.5">Source</div>
            <select className="input-field" value={form.source} onChange={e=>setForm({...form,source:e.target.value})}>
              {SOURCES.map(s=><option key={s}>{s}</option>)}
            </select></div>
          <div><div className="label-tiny mb-1.5">Product category</div>
            <select className="input-field" value={form.product_category} onChange={e=>setForm({...form,product_category:e.target.value})} data-testid="leads-category">
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select></div>
          <div><div className="label-tiny mb-1.5">Next follow-up</div>
            <input type="date" className="input-field" value={form.next_follow_up} onChange={e=>setForm({...form,next_follow_up:e.target.value})}/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1.5">Interest / requirement</div>
            <textarea rows={2} className="input-field" value={form.interest} onChange={e=>setForm({...form,interest:e.target.value})}/></div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary" data-testid="leads-submit">Save lead</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {STATUSES.map(st => {
          const cards = items.filter(i => i.status === st);
          const style = STATUS_STYLES[st] || STATUS_STYLES.new;
          return (
            <div key={st} className="card-surface p-0 overflow-hidden">
              <div className={`px-3 py-2 flex items-center justify-between ${style.header}`}>
                <div className="font-display font-bold text-xs tracking-widest">{style.label}</div>
                <span className="badge badge-neutral mono">{cards.length}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[80px]">
                {cards.map(l => (
                  <div key={l.id} className="p-3 rounded-md border border-[#EAE5DB] bg-[#FBF8F1] fade-up cursor-pointer" data-testid={`lead-card-${l.id}`} onClick={()=>setPopup(l)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-semibold text-sm truncate">{l.customer_name}</div>
                        <div className="text-xs text-[#757169] flex items-center gap-1 mt-0.5"><Phone size={10}/><span className="truncate">{l.phone||"—"}</span></div>
                      </div>
                      <Sparkles size={12} className="text-[#B3AC9E] shrink-0" />
                    </div>
                    {l.interest && <div className="text-xs mt-2 line-clamp-2">{l.interest}</div>}
                    <div className="flex items-center justify-between mt-3 flex-wrap gap-1.5" onClick={e=>e.stopPropagation()}>
                      <span className="text-[10px] text-[#757169] uppercase tracking-wider">{l.source}</span>
                      <select className="text-xs bg-transparent border border-[#EAE5DB] rounded px-1 py-0.5" value={l.status} onChange={e=>updateStatus(l.id,e.target.value)}>
                        {STATUSES.map(s=><option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap" onClick={e=>e.stopPropagation()}>
                      <WhatsAppShortcuts phone={l.phone} name={l.customer_name} variant="compact" testid={`wa-lead-${l.id}`} />
                      {l.email && (
                        <button
                          onClick={()=>mailto(l)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded-full bg-[#2C4763] text-white hover:bg-[#1F334A]"
                          data-testid={`email-lead-${l.id}`}
                          title={`Send catalog email to ${l.email}`}
                        >
                          <Mail size={10}/> Email
                        </button>
                      )}
                      <AdminDeleteButton
                        endpoint={`/leads/${l.id}`}
                        label={`lead "${l.customer_name}"`}
                        onDone={load}
                        testid={`delete-lead-${l.id}`}
                      />
                    </div>
                    {l.next_follow_up && <div className="text-[10px] text-[#757169] mt-1.5">Next: {l.next_follow_up}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)} subtitle="Lead" title={popup?.customer_name || ""}
        actions={popup && <>
          <WhatsAppShortcuts phone={popup.phone} name={popup.customer_name} variant="compact" />
          {popup.email && (
            <button onClick={()=>mailto(popup)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-[#2C4763] text-white hover:bg-[#1F334A]">
              <Mail size={12}/> Send catalog email
            </button>
          )}
        </>}>
        {popup && <>
          <div><span className="text-[#75706A]">Status:</span> <span className="badge">{popup.status}</span> · <span className="text-[#75706A]">Source:</span> {popup.source}</div>
          <div><span className="text-[#75706A]">Phone:</span> <span className="mono">{popup.phone || "—"}</span></div>
          {popup.email && <div><span className="text-[#75706A]">Email:</span> {popup.email}</div>}
          <div><span className="text-[#75706A]">Product:</span> {popup.product_category || "—"}</div>
          {popup.next_follow_up && <div><span className="text-[#75706A]">Next follow-up:</span> {popup.next_follow_up}</div>}
          {popup.interest && <div className="mt-2 pt-2 border-t border-[#E7E1D3]"><div className="label-tiny mb-1">Requirement</div>{popup.interest}</div>}
        </>}
      </DetailPopup>
    </div>
  );
}
